const normalize = (value) => String(value ?? "").trim();

export const getKichiScope = (homeBrand) =>
  [homeBrand?.restaurantCode, homeBrand?.qrCode, homeBrand?.["order-guid"] || ""]
    .map(normalize).join("::");

export const getKichiConfig = (params = []) => {
  const param = (Array.isArray(params) ? params : []).find((item) => item?.["param-code"] === "KICHI_HOTPOT");
  const groupTypes = [...new Set(normalize(param?.value).split(",")
    .map((value) => value.trim()).filter((value) => /^\d+$/.test(value)).map(Number))];
  return { enabled: Number(param?.status) === 1 && groupTypes.length > 0, groupTypes };
};

export const getKichiGroups = (categories = [], groupTypes = []) => {
  const groups = [];
  const seen = new Set();
  // Param order defines the one-compartment group first, then the two-compartment group.
  const compartments = new Map(groupTypes.map((type, index) => [Number(type), index + 1]));
  const walk = (list = []) => list.forEach((group) => {
    const groupType = Number(group?.["group-type"]);
    const compartmentCount = compartments.get(groupType);
    const key = `${groupType}:${group?.code}`;
    if (compartmentCount && !seen.has(key)) {
      seen.add(key);
      groups.push({ ...group, compartmentCount });
    }
    walk(group?.["co-menu-groups"]);
  });
  (categories || []).forEach((category) => walk(category?.["menu-groups"]));
  return groups.sort((a, b) => a.compartmentCount - b.compartmentCount);
};

export const getKichiName = (item, english) =>
  (english && item?.["name-en"]) || item?.["name-vn"] || item?.name || "";

export const getKichiPrice = (item) => Number(item?.["co-menu-item-details"]?.[0]?.price) || 0;

export const getKichiItemCodes = (groups) => new Set(
  groups.flatMap((group) => (group.items || []).map((item) => normalize(item.code))),
);

export const hasKichiInCart = (cart = [], groups = [], scope) => {
  const codes = getKichiItemCodes(groups);
  const matches = (item) => codes.has(normalize(item?.code || item?.["menu-item"]?.code));
  return cart.some((item) => {
    if (item.hotpotScope && item.hotpotScope !== scope) return false;
    if (item.hotpotBrand === "KICHI") return true;
    if (matches(item) && Number(item.quantity ?? item.qtyRealOrder ?? 1) > 0) return true;
    return (item.listItemHotpotSelected || item.listItemComboSelect || []).some(matches);
  });
};

export const hasKichiOrdered = (order, groups = []) => {
  if (!order || (order.status != null && Number(order.status) !== 1)) return false;
  const codes = getKichiItemCodes(groups);
  const matches = (item) => codes.has(normalize(item?.code || item?.["menu-item"]?.code));
  return (order.hotpot?.["list-items"] || []).some(matches) ||
    (order["lst-session"] || []).some((session) =>
      (session["lst-dish"] || []).some((item) => matches(item) && Number(item.quantity) > 0));
};

export const getAvailableKichiItems = (group, config = {}) => {
  const hidden = new Set((config["lst-show-hide-item"] || [])
    .filter((item) => item["show-in-qr-order"] === false)
    .map((item) => normalize(item["item-code"])));
  const out = new Set([
    ...(config["lst-out-of-dish"] || []).map((item) => normalize(item.code)),
    ...(config["lst-inventory-out-of-dish"] || []).map((item) => normalize(item["item-code"])),
  ]);
  return (group?.items || []).filter((item) => !hidden.has(normalize(item.code)))
    .map((item) => ({ ...item, isOutOfStock: out.has(normalize(item.code)) }));
};

export const validateKichiSelection = (selection, group, config) => {
  if (!group || selection.length !== group.compartmentCount) return "incomplete";
  const codes = selection.map((item) => normalize(item.code));
  if (new Set(codes).size !== codes.length) return "duplicate";
  const available = getAvailableKichiItems(group, config);
  if (selection.some((item) => !available.some((dish) =>
    normalize(dish.code) === normalize(item.code) && !dish.isOutOfStock))) return "unavailable";
  return "";
};

export const buildKichiCartItem = (group, selection, scope, lineItem) => {
  const items = selection.map((item, position) => ({
    ...item, position, qtyRealOrder: 1, quantity: 1, isNoFree: false,
    groupCode: group.code, groupName: group["name-vn"],
    modifiers: (item.modifiers || []).map((modifier) => ({
      ...modifier, isSelected: modifier.code === item.modifiersSelected?.code,
    })),
  }));
  return {
    code: group.code, groupCode: group.code, groupName: group["name-vn"],
    name: group["name-vn"], nameVn: group["name-vn"], nameEn: group["name-en"],
    "name-vn": group["name-vn"], "name-en": group["name-en"],
    hotpotBrand: "KICHI", hotpotScope: scope, groupType: Number(group["group-type"]),
    compartmentCount: group.compartmentCount, hotpotType: group.compartmentCount,
    "is-hotpot": true, "is-combo": true, isHotpotSpecial: true, productType: "combo",
    qtyRealOrder: 1, quantity: 1, lineItem,
    "co-menu-item-details": [{ code: group.code, price: items.reduce((sum, item) => sum + getKichiPrice(item), 0) }],
    listItemComboSelect: items, listItemHotpotSelected: items,
  };
};

// The server stores hotpotType unchanged, as it does for the existing 2/4-slot flow.
export const serializeKichiHotpot = (item) => {
  const dishs = item.listItemComboSelect.map((dish) => ({
    code: dish.code, quantity: 1, price: getKichiPrice(dish),
    modifiers: dish.modifiersSelected ? [{ code: dish.modifiersSelected.code }] : [],
    note: dish.requestNote || "", "name-vn": dish["name-vn"], "name-en": dish["name-en"],
    "group-code": item.groupCode, "group-name": item.groupName,
  }));
  return {
    dishs,
    hotpot: {
      "hotpot-type": item.compartmentCount,
      "list-items": dishs.map(({ modifiers, note, ...dish }) => ({ ...dish, name: dish["name-vn"] })),
    },
  };
};
